import type { ReactNode } from "react";
import {
  BUTTON_TYPE,
  TOOL_GROUP,
  TOOL_GROUP_ORDER,
  TOOL_ICON_BY_PROVIDER,
  TOOL_ICON_BY_TOOL_ID,
  TOOL_ICON_KIND,
  TOOL_PROVIDER_GROUP,
  TOOL_STATE,
  UI_TONE,
  WORKFLOW_STATUS
} from "../OpsCenter.Config";
import { toolRows } from "../OpsCenter.Data";
import type { LocaleCode, OpsCenterActions, OpsCenterCopy, OpsCenterState, ToolRow, UiTone } from "../OpsCenter.Types";
import { humanize, statusLabel } from "../i18n/OpsCenter.I18n";
import { SectionHeader, StatusPill, TextField } from "./OpsCenterScreen.Shared";

const ARIA_HIDDEN = "true";

interface OpsSectionProps {
  readonly state: OpsCenterState;
  readonly actions: OpsCenterActions;
  readonly copy: OpsCenterCopy;
  readonly locale: LocaleCode;
}

export function ToolInventoryDrawer({ state, actions, copy }: OpsSectionProps): ReactNode {
  const tools = toolRows(state.incident, Boolean(state.health?.ok), state.health?.toolCatalog ?? []);
  if (!state.toolDrawerOpen) {
    return null;
  }

  const filteredTools = filterTools(tools, state.toolSearch, copy);
  const involvedTools = filteredTools.filter((tool) => tool.state === TOOL_STATE.Involved);
  const groupedTools = groupTools(filteredTools.filter((tool) => tool.state !== TOOL_STATE.Involved), copy);
  return (
    <aside className="tool-drawer open" aria-label={copy.tools.title}>
      <div className="tool-panel">
        <div className="tool-drawer-top">
          <div>
            <p className="eyebrow">{copy.sections.tools}</p>
            <h2>{copy.tools.title}</h2>
          </div>
          <button aria-label={copy.actions.close} className="ai-close-button" title={copy.actions.close} type={BUTTON_TYPE.Button} onClick={actions.closeToolDrawer}>
            X
          </button>
        </div>
        <p className="muted-copy">{copy.tools.subtitle}</p>
        <TextField label={copy.tools.searchLabel} name="toolSearch" type="search" value={state.toolSearch} onChange={actions.setToolSearch} />
        <ToolGroupSection copy={copy} title={copy.tools.involvedTitle} tools={involvedTools} status={String(involvedTools.length)} />
        <div className="tool-group-list" aria-label={copy.tools.availableTitle}>
          {groupedTools.map((group) => (
            <ToolGroupSection copy={copy} key={group.id} title={group.label} tools={group.tools} status={String(group.tools.length)} />
          ))}
        </div>
        {filteredTools.length === 0 ? <p className="subtle-copy">{copy.tools.noMatches}</p> : null}
      </div>
    </aside>
  );
}

function ToolGroupSection({ copy, title, tools, status }: { readonly copy: OpsCenterCopy; readonly title: string; readonly tools: readonly ToolRow[]; readonly status: string }): ReactNode {
  if (tools.length === 0) {
    return null;
  }
  return (
    <section className="tool-group-section">
      <SectionHeader title={title} status={status} />
      <div className="tool-group-grid">
        {tools.map((tool) => <ToolRowCard copy={copy} key={tool.id} tool={tool} />)}
      </div>
    </section>
  );
}

function ToolRowCard({ copy, tool }: { readonly copy: OpsCenterCopy; readonly tool: ToolRow }): ReactNode {
  const providerLabel = toolProviderLabel(copy, tool.provider);
  return (
    <article className={`tool-pill ${tool.state}`}>
      <ToolIcon provider={tool.provider} toolId={tool.id} label={toolIconLabel(copy, tool)} />
      <div className="tool-pill-label">
        <div className="tool-pill-title-row">
          <strong>{toolLabel(copy, tool.id, tool.label)}</strong>
          <StatusPill label={statusLabel(copy, tool.state)} tone={toolTone(tool.state)} />
        </div>
        <span className="tool-pill-primary">{toolMeta(copy, tool.provider, tool.actionCount)} / {providerLabel}</span>
        <dl className="tool-pill-details">
          <div className="tool-pill-detail">
            <dt>{copy.tools.owner}</dt>
            <dd>{toolOwnerLabel(copy, tool.owner)}</dd>
          </div>
          <div className="tool-pill-detail">
            <dt>{copy.tools.scope}</dt>
            <dd>{toolScopeLabel(copy, tool.environmentScope)}</dd>
          </div>
          <div className="tool-pill-detail">
            <dt>{copy.tools.approval}</dt>
            <dd>{tool.requiresApproval ? copy.tools.approvalRequired : copy.tools.noApproval}</dd>
          </div>
          <div className="tool-pill-detail">
            <dt>{copy.tools.readiness}</dt>
            <dd>{tool.ready ? copy.tools.ready : copy.tools.notReady}</dd>
          </div>
        </dl>
      </div>
    </article>
  );
}

function filterTools(tools: readonly ToolRow[], query: string, copy: OpsCenterCopy): readonly ToolRow[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) {
    return tools;
  }
  return tools.filter((tool) =>
    [
      toolLabel(copy, tool.id, tool.label),
      toolProviderLabel(copy, tool.provider),
      toolOwnerLabel(copy, tool.owner),
      toolScopeLabel(copy, tool.environmentScope),
      tool.requiresApproval ? copy.tools.approvalRequired : copy.tools.noApproval,
      tool.ready ? copy.tools.ready : copy.tools.notReady,
      statusLabel(copy, tool.state)
    ].some((value) => value.toLowerCase().includes(normalized))
  );
}

function groupTools(tools: readonly ToolRow[], copy: OpsCenterCopy): readonly { readonly id: string; readonly label: string; readonly tools: readonly ToolRow[] }[] {
  return TOOL_GROUP_ORDER.map((groupId) => ({
    id: groupId,
    label: copy.tools[`group.${groupId}`] ?? humanize(groupId),
    tools: tools.filter((tool) => toolGroupId(tool) === groupId)
  })).filter((group) => group.tools.length > 0);
}

function toolGroupId(tool: ToolRow): string {
  return TOOL_PROVIDER_GROUP[tool.provider] ?? TOOL_GROUP.Infra;
}

function toolTone(state: string): UiTone {
  if (state === TOOL_STATE.Involved) {
    return UI_TONE.Ok;
  }
  if (state === WORKFLOW_STATUS.Blocked) {
    return UI_TONE.Critical;
  }
  return UI_TONE.Neutral;
}

function toolLabel(copy: OpsCenterCopy, toolId: string, fallback: string): string {
  return copy.toolLabels[toolId] ?? humanize(fallback || toolId);
}

function toolProviderLabel(copy: OpsCenterCopy, provider: string): string {
  return copy.toolProviders[provider] ?? humanize(provider);
}

function toolOwnerLabel(copy: OpsCenterCopy, owner: string): string {
  if (!owner) {
    return copy.status.unknown;
  }
  return copy.actorRefs[`owner:${owner}`] ?? copy.actorRefs[owner] ?? humanize(owner.replaceAll("-", "_"));
}

function toolScopeLabel(copy: OpsCenterCopy, environmentScope: readonly string[]): string {
  if (environmentScope.length === 0) {
    return copy.tools.allEnvironments;
  }
  return environmentScope.map((environment) => humanize(environment)).join(", ");
}

function toolMeta(copy: OpsCenterCopy, provider: string, actionCount: number): string {
  if (actionCount === 1) {
    return copy.toolMeta.oneAction;
  }
  if (actionCount > 1) {
    return copy.toolMeta.manyActions.replace("{count}", String(actionCount));
  }
  return copy.tools.noTypedActions ?? copy.toolProviders[provider] ?? copy.toolMeta.fallback;
}

function toolIconLabel(copy: OpsCenterCopy, tool: ToolRow): string {
  return `${toolProviderLabel(copy, tool.provider)} ${copy.tools.categoryIcon}`;
}

function ToolIcon({ provider, toolId, label }: { readonly provider: string; readonly toolId: string; readonly label: string }): ReactNode {
  const icon = toolIconKind(provider, toolId);
  return (
    <span className={`tool-icon-badge ${icon}`} aria-label={label} role="img">
      <svg className="tool-icon" viewBox="0 0 24 24" aria-hidden={ARIA_HIDDEN}>
        {toolIconPaths(icon)}
      </svg>
    </span>
  );
}

function toolIconKind(provider: string, toolId: string): string {
  return TOOL_ICON_BY_TOOL_ID[toolId] ?? TOOL_ICON_BY_PROVIDER[provider] ?? TOOL_ICON_KIND.Chart;
}

function toolIconPaths(icon: string): ReactNode {
  if (icon === TOOL_ICON_KIND.Branch) {
    return (
      <>
        <circle cx="7" cy="6" r="2" />
        <circle cx="17" cy="18" r="2" />
        <path d="M7 8v4a6 6 0 0 0 6 6h2" />
        <path d="M7 8v10" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Phone) {
    return <path d="M7 5l3 3-2 3a12 12 0 0 0 5 5l3-2 3 3-1 3c-7 1-14-6-13-13l2-2z" />;
  }
  if (icon === TOOL_ICON_KIND.Bell) {
    return (
      <>
        <path d="M7 11a5 5 0 0 1 10 0v4l2 3H5l2-3v-4z" />
        <path d="M10 20h4" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Status) {
    return (
      <>
        <rect height="14" rx="2" width="16" x="4" y="5" />
        <path d="M7 14l3-4 3 4 2-3 2 3" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Cloud) {
    return <path d="M8 18h9a4 4 0 0 0 0-8 6 6 0 0 0-11-1 4 4 0 0 0 2 9z" />;
  }
  if (icon === TOOL_ICON_KIND.Shield) {
    return (
      <>
        <path d="M12 3l7 3v5c0 5-3 8-7 10-4-2-7-5-7-10V6l7-3z" />
        <path d="M9 12l2 2 4-5" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Database) {
    return (
      <>
        <ellipse cx="12" cy="6" rx="7" ry="3" />
        <path d="M5 6v12c0 2 14 2 14 0V6" />
        <path d="M5 12c0 2 14 2 14 0" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Deploy) {
    return (
      <>
        <path d="M12 4v12" />
        <path d="M7 9l5-5 5 5" />
        <path d="M5 20h14" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Cpu) {
    return (
      <>
        <rect height="10" rx="2" width="10" x="7" y="7" />
        <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Bot) {
    return (
      <>
        <path d="M12 4v3" />
        <rect height="11" rx="4" width="14" x="5" y="8" />
        <circle cx="10" cy="13" r="1" />
        <circle cx="14" cy="13" r="1" />
        <path d="M10 16h4" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Lock) {
    return (
      <>
        <rect height="10" rx="2" width="14" x="5" y="10" />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </>
    );
  }
  if (icon === TOOL_ICON_KIND.Pulse) {
    return <path d="M4 13h4l2-6 4 12 2-6h4" />;
  }
  return (
    <>
      <path d="M5 17l4-4 3 3 6-8" />
      <path d="M5 20h14" />
    </>
  );
}
